import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';

// USED FOR AUCTIONS
function useCountdown(
  startTime: number,
  endTime: number,
  countdownType: 'start' | 'end'
) {
  const target = countdownType === 'start' ? startTime : endTime;
  const [timeLeft, setTimeLeft] = useState(
    () => target - new Date().getTime()
  );
  const router = useRouter();

  // Re-seed from the CURRENT target whenever it changes (countdownType
  // flips, or startTime/endTime themselves change). Without this, a caller
  // that switches countdownType after the hook has already mounted (e.g.
  // ProductCard's post-mount 'end' -> 'start' flip) is stuck decrementing
  // timeLeft from whatever target it was first seeded with, forever showing
  // the wrong countdown.
  useEffect(() => {
    setTimeLeft(target - new Date().getTime());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  useEffect(() => {
    if (
      countdownType === 'end' &&
      endTime < new Date().getTime() &&
      timeLeft < 0
    ) {
      return;
    }

    const intervalId = setInterval(() => {
      // Recompute remaining time from the current target every tick instead
      // of decrementing the previous state, so the displayed value can
      // never drift from -- or go stale relative to -- the actual target.
      setTimeLeft(Math.max(target - new Date().getTime(), 0));
    }, 1000);

    return () => clearInterval(intervalId);
  }, [countdownType, endTime, target, timeLeft]);

  useEffect(() => {
    if (timeLeft === 0) {
      router.replace(router.asPath);

      setTimeLeft(target - new Date().getTime());
    }
  }, [timeLeft, countdownType, router, target]);

  const days = Math.floor(timeLeft / 1000 / 60 / 60 / 24);
  const hours = Math.floor((timeLeft / 1000 / 60 / 60) % 24);
  const minutes = Math.floor((timeLeft / 1000 / 60) % 60);
  const seconds = Math.floor((timeLeft / 1000) % 60);

  return { days, hours, minutes, seconds };
}

export default useCountdown;
